/**
 * The index's CASES section and the folio group rule it shares with
 * TrackerSidebar. Split out of TrackerSidebar.tsx (914 raw lines, rule 4).
 */
import React from "react";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { Collapse, useResolvedPathname } from "./motion";
import { CHAT_ENABLED } from "./chatEnabled";
import { CASE_RESOLVED, openCaseCount } from "./caseCounts";
import { caseHeading } from "./casesDocket";
import {
  usePortfolioCasesQuery,
  type RailCase,
} from "./usePortfolioCasesQuery";

// ---------------------------------------------------------------------------
// Small-caps folio group header: hairline rule, right-aligned meta.
// ---------------------------------------------------------------------------

export const IndexGroupRule: React.FC<{
  left: React.ReactNode;
  right?: React.ReactNode;
}> = ({ left, right }) => (
  <div className="mb-1 flex items-baseline justify-between gap-2 border-b border-ink-line pb-1.5">
    <div className="flex min-w-0 items-center gap-1.5">{left}</div>
    {right ? (
      <div className="flex shrink-0 items-baseline gap-2">{right}</div>
    ) : null}
  </div>
);

export const groupTitleClass =
  "font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-ink-mute";
export const groupMetaClass =
  "font-mono text-[10px] uppercase tracking-[0.08em] text-ink-faint";
export const navRowClass =
  "flex items-baseline justify-between gap-3 rounded-md px-3 py-1.5 text-2xs text-ink motion-tint hover:bg-ink-line/40";

// ---------------------------------------------------------------------------
// CASES (FR-11): case rows link to the building case surface, never
// /portal/chat. Every company case, grouped by building (small-surface 10-31).
// ---------------------------------------------------------------------------

/** The building id on screen, or undefined off a building page. */
// eslint-disable-next-line react-refresh/only-export-components
export const useActiveBuildingId = (): string | undefined => {
  const m = useResolvedPathname().match(/^\/portal\/buildings\/([^/]+)/);
  return m ? m[1] : undefined;
};

/** Rows drawn before "Show all". */
const SHOWN = 6;

interface RailGroup {
  buildingId: string;
  label: string;
  rows: RailCase[];
}

/** The active building first, then A to Z (numeric); open before resolved, then the server's case order. */
// eslint-disable-next-line react-refresh/only-export-components
export const railGroups = (
  rows: readonly RailCase[],
  active: string | undefined,
): RailGroup[] => {
  const by = new Map<string, RailGroup>();
  rows.forEach((r) => {
    const g = by.get(r.buildingId) ?? {
      buildingId: r.buildingId,
      label: r.buildingLabel,
      rows: [],
    };
    g.rows.push(r);
    by.set(r.buildingId, g);
  });
  const resolved = (r: RailCase) => (r.status === CASE_RESOLVED ? 1 : 0);
  return [...by.values()]
    .map((g) => ({
      ...g,
      rows: g.rows
        .map((r, i) => [r, i] as const)
        .sort(([a, i], [b, j]) => resolved(a) - resolved(b) || i - j)
        .map(([r]) => r),
    }))
    .sort(
      (a, b) =>
        Number(b.buildingId === active) - Number(a.buildingId === active) ||
        a.label.localeCompare(b.label, undefined, { numeric: true }),
    );
};

const railRowLabel = (r: RailCase): string =>
  [r.caseReference, caseHeading(r)].filter((x) => x.trim()).join(" · ");

const notFound = (e: unknown): boolean =>
  (e as { response?: { status?: number } } | null)?.response?.status === 404;

/** No case, or the flag off (404), draws nothing (Leo 09-22). */
export const SidebarCasesSection: React.FC<{
  activeBuildingId: string | undefined;
}> = ({ activeBuildingId }) => {
  const { data, error, refetch } = usePortfolioCasesQuery();
  const [all, setAll] = React.useState(false);
  const list = React.useRef<HTMLDivElement>(null);
  const rows = data ?? [];
  const failed = !!error && !notFound(error);
  // Same rule as the tab: a building page prints its own count, so the rail prints none.
  const open = activeBuildingId ? undefined : openCaseCount(data);
  const show = rows.length > 0 || failed;

  let left = all ? Infinity : SHOWN;
  const groups = railGroups(rows, activeBuildingId).flatMap((g) => {
    const shown = g.rows.slice(0, Math.max(0, left));
    left -= shown.length;
    return shown.length ? [{ ...g, rows: shown }] : [];
  });

  React.useEffect(() => {
    if (all) list.current?.querySelectorAll("a")[SHOWN]?.focus();
  }, [all]);

  return (
    <Collapse open={show}>
      {show && (
        <section data-testid="tracker-cases-section" className="px-5 pt-4">
          <IndexGroupRule
            left={<h3 className={groupTitleClass}>Cases</h3>}
            right={
              <>
                {!!open && (
                  <span
                    data-testid="tracker-cases-open-count"
                    className={groupMetaClass}
                  >
                    {open} open
                  </span>
                )}
                {CHAT_ENABLED && (
                  <Link
                    to="/portal/chat"
                    data-testid="tracker-cases-to-ai"
                    className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.08em] text-ink-mute motion-tint hover:text-ink"
                  >
                    Ask the AI →
                  </Link>
                )}
              </>
            }
          />
          {failed ? (
            <p
              className="px-3 py-1.5 text-2xs text-ink-mute"
              data-testid="tracker-cases-error"
            >
              Cases did not load.{" "}
              <button
                type="button"
                className="underline motion-tint hover:text-ink"
                onClick={() => refetch()}
              >
                Retry
              </button>
            </p>
          ) : (
            // No cap here: the panel body scrolls (a 50vh cap hid 2 of Related's 9, QA 10-31).
            <div ref={list} data-testid="tracker-cases-list">
              {groups.map((g) => {
                const here = g.buildingId === activeBuildingId;
                return (
                  <div key={g.buildingId} className="pt-1">
                    <div
                      className={cn(
                        "truncate px-3 pt-1 text-2xs",
                        here ? "font-medium text-ink" : "text-ink-mute",
                      )}
                    >
                      {g.label}
                      {here && <span className="sr-only">, this building</span>}
                    </div>
                    <ul aria-label={g.label} className="space-y-0.5">
                      {g.rows.map((r) => {
                        const label = railRowLabel(r);
                        return (
                          <li key={r.caseId}>
                            <Link
                              to="/portal/buildings/$buildingId"
                              params={{ buildingId: r.buildingId }}
                              hash={`case-${r.caseId}`}
                              title={label}
                              data-testid={`tracker-case-${r.caseId}`}
                              className="block w-full rounded-md py-1 pl-5 pr-3 text-left motion-tint hover:bg-ink-line/40"
                            >
                              <span className="line-clamp-2 text-2xs text-ink">
                                {label}
                              </span>
                              {r.status === CASE_RESOLVED && (
                                <span className="text-2xs text-ink-faint">
                                  Resolved
                                </span>
                              )}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
              {!all && rows.length > SHOWN && (
                <button
                  type="button"
                  onClick={() => setAll(true)}
                  aria-label={`Show all ${rows.length} cases`}
                  data-testid="tracker-cases-show-all"
                  className="mt-1 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-ink-mute motion-tint hover:text-ink"
                >
                  Show all
                </button>
              )}
            </div>
          )}
        </section>
      )}
    </Collapse>
  );
};
